import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Wallet } from "lucide-react";
import { api, BRL, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const BulkAckBar = ({ pending, selected, setSelected, onDone }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const chosen = pending.filter((e) => selected.includes(e.id));
  const total = chosen.reduce((s, e) => s + Number(e.reserve_value || 0), 0);
  if (!chosen.length) return null;
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post("/finance/caixa/ack-bulk", { ids: chosen.map((e) => e.id) });
      toast.success(`${r.data.count} repasse(s) no caixa • ${BRL(r.data.total)}`);
      setSelected([]); setOpen(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao dar baixa"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <div className="sticky bottom-20 z-20 mt-3 rounded-xl bg-emerald-700 text-white px-4 py-3 flex items-center gap-3 shadow-lg" data-testid="bulk-ack-bar">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold opacity-90">{chosen.length} selecionado(s)</p>
          <p className="text-lg font-black tabular-nums leading-tight" data-testid="bulk-ack-total">{BRL(total)}</p>
        </div>
        <Button onClick={() => setOpen(true)} className="h-10 bg-white text-emerald-800 hover:bg-emerald-50 font-black" data-testid="bulk-ack-open">
          <Wallet className="w-4 h-4 mr-1.5" /> Adicionar ao caixa
        </Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col w-[calc(100vw-1.5rem)] sm:w-full [&>*]:min-w-0" data-testid="bulk-ack-dialog">
          <DialogHeader className="text-left shrink-0"><DialogTitle className="font-heading font-black text-emerald-800">Transferir para o caixa</DialogTitle></DialogHeader>
          <ul className="divide-y divide-slate-100 overflow-y-auto min-h-0 flex-1">
            {chosen.map((e) => (
              <li key={e.id} className="py-2 flex items-center gap-2 text-[13px]">
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-900 truncate">Nº {e.budget_number} — {e.school_name || "—"}</p>
                  <p className="text-[10.5px] text-slate-500">NF {e.nf_number || "—"} • {formatDateBR(e.nf_date)}</p>
                </div>
                <span className="font-black tabular-nums text-emerald-700 shrink-0">{BRL(e.reserve_value)}</span>
              </li>
            ))}
          </ul>
          <div className="shrink-0 space-y-3">
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-center">
              <p className="text-[11px] font-bold text-emerald-700 uppercase">Valor a transferir</p>
              <p className="text-2xl font-black tabular-nums text-emerald-800" data-testid="bulk-ack-dialog-total">{BRL(total)}</p>
              <p className="text-[11px] text-emerald-800/80 mt-1">Faça a transferência e confirme para dar baixa nestes {chosen.length} orçamento(s).</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} className="flex-1">Cancelar</Button>
              <Button onClick={confirm} disabled={busy} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-black" data-testid="bulk-ack-confirm">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Transferi, dar baixa</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
